import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Phone, ArrowLeft, Loader2, CheckCircle, KeyRound, Lock, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const COUNTRY_CODES = [
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+1', country: 'USA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+233', country: 'Ghana', flag: '🇬🇭' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
];

export default function ForgotPasswordSMS() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter Phone, 2: Enter OTP, 3: New Password
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+234');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!phone) {
      toast.error('Please enter your phone number');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/password-reset/send-otp`, {
        phone,
        country_code: countryCode
      });
      toast.success(res.data?.channel === 'whatsapp' ? 'OTP sent via WhatsApp!' : 'OTP sent to your phone!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 4) {
      toast.error('Please enter the OTP code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/password-reset/verify-otp`, {
        phone,
        country_code: countryCode,
        code: otp
      });
      
      if (response.data.valid) {
        setResetToken(response.data.reset_token);
        toast.success('OTP verified! Set your new password.');
        setStep(3);
      } else {
        toast.error('Invalid OTP code');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/confirm`, {
        phone,
        country_code: countryCode,
        reset_token: resetToken,
        new_password: newPassword
      });
      
      toast.success('Password reset successful!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <form onSubmit={handleSendOTP} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number</Label>
        <div className="flex gap-2">
          <Select value={countryCode} onValueChange={setCountryCode}>
            <SelectTrigger className="w-[120px] h-12 rounded-xl" data-testid="country-code-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_CODES.map((cc) => (
                <SelectItem key={cc.code} value={cc.code}>
                  {cc.flag} {cc.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="phone"
              type="tel"
              placeholder="8012345678"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
              required
              className="pl-10 h-12 rounded-xl"
              data-testid="reset-phone-input"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Enter the phone number linked to your account</p>
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 rounded-xl gap-2"
        disabled={loading}
        data-testid="send-otp-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending OTP...
          </>
        ) : (
          <>
            <Phone className="w-4 h-4" />
            Send OTP
          </>
        )}
      </Button>
    </form>
  );

  const renderStep2 = () => (
    <form onSubmit={handleVerifyOTP} className="space-y-6">
      <Alert className="bg-blue-500/10 border-blue-500/20">
        <Phone className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-200">
          We've sent a 6-digit code to {countryCode}{phone}
        </AlertDescription>
      </Alert>
      
      <div className="space-y-2">
        <Label htmlFor="otp">Enter OTP Code</Label>
        <Input
          id="otp"
          type="text"
          placeholder="123456"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          required
          maxLength={6}
          className="h-14 text-center text-2xl tracking-widest rounded-xl"
          data-testid="otp-input"
        />
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 rounded-xl gap-2"
        disabled={loading || otp.length < 4}
        data-testid="verify-otp-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Verifying...
          </>
        ) : (
          <>
            <CheckCircle className="w-4 h-4" />
            Verify OTP
          </>
        )}
      </Button>

      <Button 
        type="button"
        variant="ghost" 
        className="w-full"
        onClick={() => setStep(1)}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Change phone number
      </Button>
    </form>
  );

  const renderStep3 = () => (
    <form onSubmit={handleResetPassword} className="space-y-6">
      <Alert className="bg-green-500/10 border-green-500/20">
        <CheckCircle className="h-4 w-4 text-green-500" />
        <AlertDescription className="text-green-200">
          Phone verified! Create your new password.
        </AlertDescription>
      </Alert>
      
      <div className="space-y-2">
        <Label htmlFor="newPassword">New Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="newPassword"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            className="pl-10 pr-10 h-12 rounded-xl"
            data-testid="new-password-input"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="pl-10 h-12 rounded-xl"
            data-testid="confirm-password-input"
          />
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 rounded-xl gap-2"
        disabled={loading}
        data-testid="reset-password-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Resetting Password...
          </>
        ) : (
          <>
            <KeyRound className="w-4 h-4" />
            Reset Password
          </>
        )}
      </Button>
    </form>
  );

  return (
    <Layout>
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-card/50 backdrop-blur border-border/50">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <KeyRound className="w-8 h-8 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">
                {step === 1 && 'Forgot Password?'}
                {step === 2 && 'Enter OTP'}
                {step === 3 && 'New Password'}
              </CardTitle>
              <CardDescription className="mt-2">
                {step === 1 && "We'll send you an OTP to reset your password"}
                {step === 2 && "Enter the code we sent to your phone"}
                {step === 3 && "Choose a strong password for your account"}
              </CardDescription>
            </div>
            
            {/* Step indicator */}
            <div className="flex justify-center gap-2 pt-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`w-8 h-1 rounded-full transition-colors ${
                    s <= step ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </CardHeader>
          
          <CardContent>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}

            <div className="text-center mt-6">
              <Link 
                to="/login" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
